import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Minimal drop-in replacement for the `MediaObserver` API previously provided by
 * `@ngbracket/ngx-layout` (which the project removed). It exposes only the surface the
 * codebase actually used — `isActive(alias)` and `asObservable()` — backed by the native
 * `window.matchMedia`, and reproduces ngx-layout's exact breakpoint media queries and
 * alias priorities so responsive behaviour is unchanged.
 */
export interface FuseMediaChange {
  mqAlias: string;
  mediaQuery: string;
  matches: boolean;
}

interface Breakpoint {
  alias: string;
  mediaQuery: string;
  priority: number;
}

// Mirrors DEFAULT_BREAKPOINTS from @ngbracket/ngx-layout (core).
const BREAKPOINTS: Breakpoint[] = [
  { alias: 'xs', mediaQuery: 'screen and (min-width: 0px) and (max-width: 599.98px)', priority: 1000 },
  { alias: 'sm', mediaQuery: 'screen and (min-width: 600px) and (max-width: 959.98px)', priority: 900 },
  { alias: 'md', mediaQuery: 'screen and (min-width: 960px) and (max-width: 1279.98px)', priority: 800 },
  { alias: 'lg', mediaQuery: 'screen and (min-width: 1280px) and (max-width: 1919.98px)', priority: 700 },
  { alias: 'xl', mediaQuery: 'screen and (min-width: 1920px) and (max-width: 4999.98px)', priority: 600 },
  { alias: 'lt-sm', mediaQuery: 'screen and (max-width: 599.98px)', priority: 950 },
  { alias: 'lt-md', mediaQuery: 'screen and (max-width: 959.98px)', priority: 850 },
  { alias: 'lt-lg', mediaQuery: 'screen and (max-width: 1279.98px)', priority: 750 },
  { alias: 'lt-xl', mediaQuery: 'screen and (max-width: 1919.98px)', priority: 650 },
  { alias: 'gt-xs', mediaQuery: 'screen and (min-width: 600px)', priority: -950 },
  { alias: 'gt-sm', mediaQuery: 'screen and (min-width: 960px)', priority: -850 },
  { alias: 'gt-md', mediaQuery: 'screen and (min-width: 1280px)', priority: -750 },
  { alias: 'gt-lg', mediaQuery: 'screen and (min-width: 1920px)', priority: -650 },
];

@Injectable({ providedIn: 'root' })
export class FuseMediaObserver implements OnDestroy {
  private readonly _byAlias = new Map<string, MediaQueryList>();
  private readonly _change = new BehaviorSubject<FuseMediaChange[]>([]);
  private readonly _listeners: Array<{ mql: MediaQueryList; fn: (e: MediaQueryListEvent) => void }> = [];

  public constructor(private readonly _zone: NgZone) {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }
    for (const bp of BREAKPOINTS) {
      const mql = window.matchMedia(bp.mediaQuery);
      this._byAlias.set(bp.alias, mql);
      const fn = (): void => this._zone.run(() => this._emit());
      mql.addEventListener('change', fn);
      this._listeners.push({ mql, fn });
    }
    this._emit(); // seed the current state so new subscribers get it immediately (like ngx-layout)
  }

  /** Emits the active breakpoints (highest priority first) whenever the active set changes. */
  public asObservable(): Observable<FuseMediaChange[]> {
    return this._change.asObservable();
  }

  /** True when the breakpoint(s) for `alias` currently match (alias may be space/comma separated). */
  public isActive(alias: string): boolean {
    return String(alias)
      .split(/[\s,]+/)
      .filter(Boolean)
      .some((a) => this._byAlias.get(a)?.matches ?? false);
  }

  public ngOnDestroy(): void {
    for (const { mql, fn } of this._listeners) {
      mql.removeEventListener('change', fn);
    }
    this._change.complete();
  }

  private _emit(): void {
    const active = BREAKPOINTS.filter((bp) => this._byAlias.get(bp.alias)?.matches)
      .sort((a, b) => b.priority - a.priority)
      .map((bp) => ({ mqAlias: bp.alias, mediaQuery: bp.mediaQuery, matches: true }));
    this._change.next(active);
  }
}
